import every from 'lodash/every';
import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Table, Button } from 'react-bootstrap';

import { withFormContext } from './FormContext';
import FormTableBody from './FormTableBody';
import FormTableButton from './table/FormTableButton';
import FormTableHeading from './FormTableHeading';
import FormTableHelp from './table/FormTableHelp';
import FormTablePagination from './table/FormTablePagination';
import FormTableToolbar from './table/FormTableToolbar';
import FormTableTotals from './table/FormTableTotals';
import './styles/table.scss';

const _ = { every };

class FormTable extends Component {
  constructor(props) {
    super(props);

    this.addItem = this.addItem.bind(this);
    this.handleAddKey = this.handleAddKey.bind(this);
    this.handleBatchInput = this.handleBatchInput.bind(this);
    this.handleColumnCheck = this.handleColumnCheck.bind(this);
    this.handleCollapseAll = this.handleCollapseAll.bind(this);
    this.removeItem = this.removeItem.bind(this);
    this.hasTotalsRow = this.hasTotalsRow.bind(this);
    this.setPage = this.setPage.bind(this);
    this.clearFilter = this.clearFilter.bind(this);
    this.isSectionHidden = this.isSectionHidden.bind(this);
    this.sortColumn = this.sortColumn.bind(this);
    this.formManager = props.context.manager;
    this.ee = props.context.ee;
    this.collapse = this.collapse.bind(this);

    // Find Sections
    this.sections = this.formManager.layout.getAllSectionsUnderTable(props.controlId);

    // Find Base Columns
    const baseColumns = [];
    this.formManager.layout.getAllFieldsUnderGroup(props.controlId).forEach(fieldName => {
      const config = props.form[fieldName];
      if (config.parentId === props.controlId && config.aggregated !== true) {
        baseColumns.push(fieldName);
      }
    });

    this.totalsRowPresent = this.hasTotalsRow();
    this.sectionCount = this.getSectionCount();
    this.baseColumns = baseColumns;

    this.formTableBody = React.createRef();

    this.manager = this.formManager.tables[props.controlId];
    this.manager.limit = props.limit;
    this.manager.threshold = props.threshold;

    this.state = {
      batchInputValue: 1,
      currentIndex: 0,
      collapsed: {},
    };

    this.prepare(props);
  }

  componentDidMount() {
    const { controlId } = this.props;

    if (this.ee) {
      this.ee.on(controlId, (...args) => {
        setTimeout(() => {
          this.consume(...args);
        }, 10);
      });
    }
  }

  componentWillUnmount() {
    const { controlId } = this.props;

    if (this.ee) {
      this.ee.off(controlId);
    }
  }

  shouldComponentUpdate(newProps, nextState) {
    this.manager = this.formManager.tables[newProps.controlId];
    this.prepare(newProps, nextState);
    return true;
  }

  consume = ({ action, payload }) => {
    if (action === 'reload') {
      if (payload && payload.form) {
        this.manager.masterForm = payload.form;
        this.manager.cacheDepList().checkDeps();
      }

      this.forceUpdate(() => {
        this.formTableBody.current.reload();
      });
    }
  };

  prepare(newProps, nextState) {
    const { form } = newProps;
    this.manager.masterForm = form;
    this.manager.cacheDepList().checkDeps();

    if (nextState && this.state.currentIndex !== nextState.currentIndex) {
      this.manager.setBoundaries(nextState.currentIndex);
    }
  }

  /**
   * Columns/fields that are visible
   *
   * @return {Array} - List of field names.
   */
  getVisibleColumns() {
    const { form } = this.props;

    const visibleColumns = [];
    const columns = this.baseColumns;

    columns.forEach(fieldName => {
      if (form[fieldName].hidden) {
        return;
      }

      visibleColumns.push(fieldName);
    });

    return visibleColumns;
  }

  /**
   * Total number of sections inside table.
   *
   * @return {Number}
   */
  getSectionCount() {
    return Object.keys(this.sections).length;
  }

  /**
   * Sets page when pagination is available.
   *
   * @param {Number} index - New starting index.
   * @return {Number}
   */
  setPage(index) {
    this.setState({ currentIndex: index });
  }

  jumpToLast() {
    const { limit } = this.props;
    const { count } = this.manager.getCount();
    return this.setPage((Math.floor(count / limit) + Math.sign(count % limit) - 1) * limit);
  }

  /**
   * Determines if adding a new row is allowed.
   *
   * @return {Boolean}
   */
  canAdd() {
    const { canAdd, maxSize } = this.props;
    const { count } = this.manager.getCount();

    if (!canAdd || (maxSize && count >= maxSize)) {
      return false;
    }

    return true;
  }

  /**
   * Checkbox handler for column checkbox.
   *
   * @param {Object} event
   * @param {String} fieldName - Name of the field.
   * @return
   */
  handleColumnCheck(event, fieldName) {
    const { checked } = event.target;
    const callback = () => {
      this.manager.clearCache();
      this.formTableBody.current.reload();
    };

    this.props.checkAll(fieldName, checked, callback.bind(this));
  }

  handleCollapseAll() {
    const collapseMap = {};
    const collapseValue = !this.areAllRowsCollapsed();

    Object.keys(this.manager.rowIds).forEach(rowId => {
      collapseMap[rowId] = collapseValue;
    });
    this.setState({ collapsed: collapseMap });
  }

  areAllRowsCollapsed() {
    return Object.keys(this.state.collapsed).length > 0
      ? Object.keys(this.state.collapsed).every(rowId => this.state.collapsed[rowId] === true)
      : false;
  }

  collapse(rowId) {
    this.setState(({ collapsed }) => ({
      collapsed: { ...collapsed, [rowId]: collapsed[rowId] === undefined ? true : !collapsed[rowId] },
    }));
  }

  /**
   * Sort column by field name.
   *
   * @param {String} fieldName
   * @return {String} Sort direction
   */
  sortColumn(fieldName) {
    this.manager.sortBy(fieldName);
    this.forceUpdate(() => {
      this.formTableBody.current.reload();
    });

    return this.manager.sortDirection;
  }

  handleBatchInput(event) {
    const batchInputValue = event.target.value.replace(/\D/g, '');
    if (batchInputValue.length >= 4) {
      return;
    }
    this.setState({
      batchInputValue,
    });
  }

  /**
   * Determines if a section is hidden or not.
   *
   * @param {String} section - Name of the section.
   * @param {Number} dataIndex
   * @return {Boolean}
   */
  isSectionHidden(sectionName, dataIndex) {
    const { form } = this.props;

    if (!sectionName) {
      return false;
    }

    // TODO This should only use the prop going forward
    // if (this.sections[sectionName].hidden !== undefined) {
    //   return this.sections[sectionName].hidden;
    // }

    const fields = this.formManager.layout.getAllFieldsUnderGroup(sectionName);
    return fields.every(fieldName => form[fieldName].hiddenIndex[dataIndex] === true);
  }

  /**
   * Determines if a the entire collection itself is hidden.
   *
   * @return {Boolean}
   */
  isHidden() {
    const { form, hideOnEmpty } = this.props;
    const { count } = this.manager.getCount();

    if (hideOnEmpty && count === 0) {
      return true;
    }

    // TODO This should only use the prop going forward
    if (this.props.hidden !== undefined) {
      return this.props.hidden;
    }

    // Old logic to check the show/hide status of every field
    let hidden = true;
    this.manager.fieldNameList.forEach(fieldName => {
      if (hidden) {
        if (form[fieldName].hiddenIndex.length && _.every(form[fieldName].hiddenIndex, v => v === false)) {
          hidden = false;
        } else {
          hidden = form[fieldName].hidden; // eslint-disable-line
        }
      }
    });

    return hidden;
  }

  /**
   * Add a new row to the collection.
   *
   * @param {Boolean} autoFocus - Autofocus the first field of the new row.
   * @return
   */
  addItem(index, autoFocus = true) {
    let newIndex, isLast, newId;

    for (let i = 1; i <= this.state.batchInputValue; i++) {
      if (this.canAdd()) {
        newIndex = index !== undefined ? index : this.manager.getRows().length || 0;
        isLast = newIndex === this.manager.getRows().length;
        this.props.context.handleAddItem(this.manager.name, newIndex);
        newId = this.manager.getRowIdByDataIndex(newIndex);
      }
    }

    this.formTableBody.current.reload(() => {
      if (autoFocus) {
        setTimeout(() => {
          this.autoFocus(newId);
        }, 10);
      }

      if (this.isPaginated()) {
        if (isLast) {
          this.jumpToLast();
        } else {
          this.setPage(this.props.limit);
        }
      }
    });
  }

  /**
   * Keypress handler for add item button.
   *
   * @param {Object} event
   * @return
   */
  handleAddKey(event) {
    if (event.keyCode === 13 || event.keyCode === 32) {
      this.addItem();
    }
  }

  /**
   * Autofocus the most appropriate row.
   *
   * @return
   */
  autoFocus(rowId) {
    const { controlId } = this.props;

    try {
      const selector = `#${controlId} #row-id-${rowId}.primary.data-row .form-group.is-enabled .form-control:first-of-type`;
      document.querySelector(selector).focus();
    } catch (err) {
      console.warn('Autofocus failed', err.message);
    }
  }

  /**
   * Remove existing row.
   *
   * @param {Number} dataIndex - Row index.
   * @return
   */
  removeItem(rowId) {
    const { limit } = this.props;
    const dataIndex = this.manager.getDataIndexByRowId(rowId);
    const form = this.props.onRemoveItem(this.manager.name, dataIndex);

    // TODO Could be less ghetto
    this.manager.masterForm = form;
    this.manager.removeRow(rowId);

    const { count } = this.manager.getCount();

    // Change page if necessary
    if (this.isPaginated() || count === limit) {
      if (this.state.currentIndex >= count) {
        this.setState(prevState => ({ currentIndex: prevState.currentIndex - limit }));
      }
    }

    this.forceUpdate();
  }

  /**
   * Pops a modal to filter the table,
   *
   * @return
   */
  filterTable() {
    const { onTableFilter } = this.props.context;

    if (!onTableFilter) {
      return;
    }

    const result = onTableFilter(this.props);

    if (result && result.then) {
      result.then(payload => {
        if (payload) {
          this.manager.applyFilter(payload);
          this.forceUpdate(() => {
            this.formTableBody.current.reload();
          });
        }
      });
    }
  }

  /**
   * Clears any applied filters.
   *
   * @return
   */
  clearFilter() {
    this.manager.clearFilter();
    this.forceUpdate(() => {
      this.formTableBody.current.reload();
    });
  }

  /**
   * Determines if there is a totals row.
   *
   * @return {Boolean}
   */
  hasTotalsRow() {
    const { form } = this.props;
    let result = false;

    Object.keys(form).forEach(fieldName => {
      if (form[fieldName].aggregate) {
        result = true;
      }
    });

    return result;
  }

  /**
   * Determines if the pagination should be active.
   *
   * @return {Boolean}
   */
  isPaginated() {
    const { threshold, limit } = this.props;
    const { count } = this.manager.getCount();
    return count >= threshold && count > limit;
  }

  renderToolbar() {
    const { title } = this.props;
    const { filtered, total } = this.manager.getCount();

    return (
      <FormTableToolbar>
        {total > 0 && (
          <FormTableButton title="Filter Options" onClick={() => this.filterTable()} badge={filtered || undefined}>
            <i className="zmdi zmdi-filter-list" />
          </FormTableButton>
        )}
        {this.manager.hasFilter() && (
          <FormTableButton className="clear-filter" title="Clear Filter" onClick={() => this.clearFilter()}>
            <i className="zmdi zmdi-close" />
          </FormTableButton>
        )}
        {title && (
          <FormTableButton className="title" title={title}>
            {title}
          </FormTableButton>
        )}
      </FormTableToolbar>
    );
  }

  renderColGroup() {
    const { form } = this.props;

    return (
      <colgroup>
        <col span="3" />
        {this.baseColumns.map(fieldName => {
          const field = form[fieldName];

          if (!field) {
            console.log('Unknown field', fieldName, form);
          }

          if (field.hiddenIndex && field.hiddenIndex.length && field.hiddenIndex.every(v => v === true)) {
            return <col key={fieldName} className="hidden" />;
          }

          if (this.manager.sortedField === fieldName) {
            return <col key={fieldName} className="sorted" />;
          }

          return <col key={fieldName} />;
        })}
      </colgroup>
    );
  }

  renderBody() {
    const { count } = this.manager.getCount();

    const { ...rest } = this.props;

    return (
      <>
        <FormTableBody
          ref={this.formTableBody}
          addItem={this.addItem}
          baseColumns={this.baseColumns}
          canAdd={this.canAdd()}
          className="base-body"
          currentIndex={this.state.currentIndex}
          fieldNameList={this.manager.fieldNameList}
          isSectionHidden={this.isSectionHidden}
          sections={this.sections}
          manager={this.manager}
          removeItem={this.removeItem}
          sectionCount={this.sectionCount}
          sortColumn={this.sortColumn}
          collapsed={this.state.collapsed}
          collapse={this.collapse}
          {...rest}
        />
        {this.totalsRowPresent && (
          <FormTableTotals columns={this.getVisibleColumns()} form={this.props.form} rows={count} />
        )}
      </>
    );
  }

  renderAddControls() {
    const { canBatchAdd } = this.props;
    const { count } = this.manager.getCount();
    if (!count || !this.canAdd()) {
      return null;
    }

    if (canBatchAdd) {
      return (
        <div className="add-control">
          <div className="add-button">
            <div className="batch-add">
              <Button
                variant="success"
                size="sm"
                onKeyUp={this.handleAddKey}
                onMouseDown={() => this.addItem()}
                disabled={!this.canAdd()}
              >
                <i className="zmdi zmdi-plus zmdi-hc-lg" /> Add
                <input
                  className="input-rows"
                  onMouseDown={e => e.stopPropagation()}
                  onChange={this.handleBatchInput}
                  value={this.state.batchInputValue}
                />
                Rows
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="add-control">
        <div className="add-button">
          <Button
            size="sm"
            variant="success"
            onKeyUp={this.handleAddKey}
            onMouseDown={() => this.addItem()}
            disabled={!this.canAdd()}
          >
            <i className="zmdi zmdi-plus zmdi-hc-lg" /> Add New
          </Button>
        </div>
        <div className="add-block">
          <Button
            variant="success"
            onKeyUp={this.handleAddKey}
            onMouseDown={() => this.addItem()}
            disabled={!this.canAdd()}
          >
            <i className="zmdi zmdi-plus" />
          </Button>
        </div>
      </div>
    );
  }

  renderCount() {
    const { deleted, count } = this.manager.getCount();

    return (
      <FormTableToolbar className="right">
        <FormTableButton className="title" title="Row Count">
          {count}
        </FormTableButton>
        {deleted !== 0 && (
          <FormTableButton
            className="title deleted"
            title={`${deleted} row${deleted === 1 ? '' : 's'} marked for removal`}
          >
            {deleted}
          </FormTableButton>
        )}
        {this.canAdd() === true && (
          <FormTableButton
            className="quick-add"
            title="Quick Add"
            onClick={() => {
              this.addItem(undefined, false);
            }}
          >
            <i className="zmdi zmdi-collection-plus" />
          </FormTableButton>
        )}
      </FormTableToolbar>
    );
  }

  render() {
    const {
      canDelete,
      canSort,
      collapsibleRows,
      collapsibleText,
      collection,
      controlId,
      form,
      limit,
      repeatHeaders,
      title,
    } = this.props;

    const { count } = this.manager.getCount();

    const className = classNames('form-table', {
      'has-title': !!title,
      'is-hidden': this.isHidden(),
      'has-totals': this.totalsRowPresent,
      'can-delete': canDelete,
      'headers-repeat': repeatHeaders,
    });

    return (
      <div id={controlId} className={className}>
        {this.renderToolbar()}
        {this.renderCount()}
        <FormTableHelp items={collection.map(field => ({ label: field.label, helpText: field.helpText }))} />
        <div className="table-wrapper">
          <Table cellSpacing={0}>
            {this.renderColGroup()}
            <thead>
              {count > 0 && (
                <FormTableHeading
                  allRowsCollapsed={this.areAllRowsCollapsed()}
                  columns={this.baseColumns}
                  form={form}
                  collapsibleRows={collapsibleRows}
                  collapsibleText={collapsibleText}
                  onColumnCheck={this.handleColumnCheck}
                  onCollapseAll={this.handleCollapseAll}
                  manager={this.manager}
                  sortColumn={this.sortColumn}
                  sortable={count > 1 && canSort}
                />
              )}
            </thead>
            {this.renderBody()}
          </Table>
        </div>
        {this.isPaginated() && (
          <FormTablePagination index={this.state.currentIndex} limit={limit} setPage={this.setPage} size={count} />
        )}
        {this.renderAddControls()}
        <div className="clearfix" />
      </div>
    );
  }
}

FormTable.defaultProps = {
  canAdd: true,
  canBatchAdd: false,
  canDelete: true,
  canInsert: false,
  canSort: true,
  checkAll: () => {},
  collapsibleRows: false,
  hideOnEmpty: false,
  initialSize: 0,
  limit: 10,
  maxSize: null,
  minSize: null,
  numberedRows: false,
  onRemoveItem: () => {},
  repeatHeaders: false,
  threshold: 50,
  title: '',
};

FormTable.propTypes = {
  canAdd: PropTypes.bool,
  canBatchAdd: PropTypes.bool,
  canDelete: PropTypes.bool,
  canInsert: PropTypes.bool,
  canSort: PropTypes.bool,
  checkAll: PropTypes.func,
  collapsibleRows: PropTypes.bool,
  collapsibleText: PropTypes.string,
  collection: PropTypes.array.isRequired,
  context: PropTypes.object.isRequired,
  controlId: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  hidden: PropTypes.bool,
  hideOnEmpty: PropTypes.bool,
  initialSize: PropTypes.number,
  limit: PropTypes.number,
  maxSize: PropTypes.number,
  minSize: PropTypes.number,
  numberedRows: PropTypes.bool,
  onRemoveItem: PropTypes.func,
  repeatHeaders: PropTypes.bool,
  threshold: PropTypes.number,
  title: PropTypes.string,
};

export default withFormContext(FormTable);
